// src/pages/TravelDashboard.js
"use client"
import { useState } from "react"
import { toast } from "react-toastify"
import TravelRequestCard from "../../components/operationsAssociate/TravelRequestCard"
import RejectModal from "../../components/operationsAssociate/TravelRejectModal"
import Sidebar from "../../components/operationsAssociate/OASidebar"
import TopbarNav from "../../components/TopbarNav"
import DashboardSummary from "./TravelSummarystats"
import AddItineraryModal from "../../components/head-of-Operations/AddItenaryModal"
import useTravelRequests from "../../hooks/useTravelRequest"

const TravelDashboard = () => {
  const {
    requests,
    loading,
    error,
    summary,
    updateRequestStatus,
    addItinerary,
    refreshRequests,
    fetchTravelRequests,
  } = useTravelRequests()

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [filters, setFilters] = useState({
    status: "",
    dateRange: "",
    destination: "",
  })

  // Sidebar state for responsiveness
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleApprove = async (id) => {
    try {
      await updateRequestStatus(id, "approved")
      toast.success("Travel request approved successfully!")
    } catch (error) {
      toast.error("Failed to approve travel request.")
    }
  }

  const handleReject = (id) => {
    setSelectedRequestId(id)
    setIsRejectModalOpen(true)
  }

  const handleRejectSubmit = async (reason) => {
    try {
      await updateRequestStatus(selectedRequestId, "rejected", { rejectionReason: reason })
      setIsRejectModalOpen(false)
      setSelectedRequestId(null)
      toast.success("Travel request rejected successfully!")
    } catch (error) {
      toast.error("Failed to reject travel request.")
    }
  }

  const handleAddItinerary = (id) => {
    setSelectedRequestId(id)
    setIsItineraryModalOpen(true)
  }

  const handleItinerarySubmit = async (itineraryData) => {
    try {
      await addItinerary(selectedRequestId, {
        ...itineraryData,
        bookedBy: "operations_associate_id",
      })
      setIsItineraryModalOpen(false)
      setSelectedRequestId(null)
      toast.success("Itinerary added successfully!")
    } catch (error) {
      toast.error("Failed to add itinerary.")
    }
  }

  // Add a stub function for review report to prevent errors
  const handleReviewReport = (id) => {
    console.log("Review report for request:", id)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    fetchTravelRequests(newFilters)
  }

  const handleLogout = () => {
    localStorage.removeItem("token")
    window.location.href = "/"
  }

  if (loading && requests.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 font-sans">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 rounded-full border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading travel requests...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Sidebar */}
      <Sidebar
        visible={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
      />

      {/* Main Area */}
      <div className="flex flex-col flex-1 md:ml-64">
        <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 p-6 space-y-8 pt-20 overflow-y-auto">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
            <h1 className="text-3xl font-bold text-gray-900">Travel Management System</h1>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex justify-between items-center">
              <span>{error}</span>
              <button onClick={refreshRequests} className="ml-4 text-red-800 underline">
                Retry
              </button>
            </div>
          )}

          <DashboardSummary summaryData={summary} />

          {/* Filters Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Filter Requests</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange({ ...filters, status: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
                <input
                  type="text"
                  placeholder="Destination"
                  value={filters.destination}
                  onChange={(e) => handleFilterChange({ ...filters, destination: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange({ ...filters, dateRange: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Dates</option>
                  <option value="upcoming">Upcoming (7 days)</option>
                  <option value="this_month">This Month</option>
                  <option value="past">Past Travels</option>
                </select>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Request Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-2xl font-bold text-blue-700">{summary.pending}</p>
                <p className="text-sm text-blue-600">Pending Requests</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-2xl font-bold text-green-700">{summary.approved}</p>
                <p className="text-sm text-green-600">Approved Requests</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-2xl font-bold text-purple-700">{summary.withItinerary}</p>
                <p className="text-sm text-purple-600">With Itinerary</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-2xl font-bold text-amber-700">{summary.upcoming}</p>
                <p className="text-sm text-amber-600">Upcoming Travels</p>
              </div>
            </div>
          </div>

          {/* Travel Requests List */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Travel Requests</h2>
              <p className="text-sm text-gray-500">
                Showing {requests.length} travel request{requests.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            {requests.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-600">No travel requests found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {requests.map((request) => (
                  <TravelRequestCard
                    key={request._id}
                    request={request}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onAddItinerary={handleAddItinerary}
                    onReviewReport={handleReviewReport}
                  />
                ))}
              </div>
            )}
          </div>

          {loading && requests.length > 0 && (
            <div className="text-center mt-4">
              <div className="animate-spin h-6 w-6 border-2 rounded-full border-orange-500 border-t-transparent mx-auto"></div>
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      <RejectModal
        isOpen={isRejectModalOpen}
        onClose={() => {
          setIsRejectModalOpen(false)
          setSelectedRequestId(null)
        }}
        onSubmit={handleRejectSubmit}
      />

      <AddItineraryModal
        isOpen={isItineraryModalOpen}
        onClose={() => {
          setIsItineraryModalOpen(false)
          setSelectedRequestId(null)
        }}
        onSubmit={handleItinerarySubmit}
      />
    </div>
  )
}

export default TravelDashboard